// Carregamento do SDK de prescrição da Memed.
//
// Regra do módulo: o SDK só entra na página que vai prescrever, e no momento em
// que o médico pede. Ele não é inofensivo — cada injeção instala ~7 iframes e um
// listener global de `message` no `window`, que passam a dividir documento e
// main thread com o resto da aplicação. Carregá-lo no login, como era feito
// antes, fazia uma recusa da Memed (401 de médico não aprovado) derrubar telas
// que não têm nada a ver com prescrição.

export const MEMED_SCRIPT_ID = "memed-script";
export const MEMED_CONTAINER_ID = "memed-container";

// Container que a própria Memed cria e mantém escondido até terminar de
// inicializar. É o nosso único sinal observável de que o SDK ficou pronto — o
// 401 acontece dentro de iframes cross-origin, invisível para nós.
export const MEMED_MODULE_CONTAINER_ID = "iframe-container";

export const MEMED_BLOCKED_MESSAGE =
  "Sua conta ainda não permite realizar essa ação";

export const MEMED_UNAVAILABLE_MESSAGE =
  "A prescrição está indisponível no momento. Tente novamente em instantes.";

// Só o backend enxerga a recusa da Memed — o 401 acontece na API deles. Daqui
// o máximo observável é "o módulo não abriu", então sem uma causa vinda do
// servidor a leitura mais provável é conta sem permissão.
const UNAVAILABLE_CAUSES = [
  "memed:unavailable",
  "memed:script-error",
  "memed:hub-timeout"
];

export function memedMessageFor(error) {
  const cause = error && error.message;
  return UNAVAILABLE_CAUSES.includes(cause)
    ? MEMED_UNAVAILABLE_MESSAGE
    : MEMED_BLOCKED_MESSAGE;
}

export const MEMED_HUB_TIMEOUT_MS = 15000;
const HUB_POLL_INTERVAL_MS = 120;

/**
 * Injeta o SDK. A URL vem SEMPRE do env (VUE_APP_DOMINIO_API_MEMED) — antes
 * Login.vue/Register.vue tinham a URL sandbox hardcoded, o que ignorava a
 * config de ambiente. Devolve o `<script>` já existente quando há um, para que
 * dois chamadores concorrentes não dupliquem o SDK.
 */
export function loadMemedScript(token, doc = document) {
  if (!token) return null;

  const existing = doc.getElementById(MEMED_SCRIPT_ID);
  if (existing) return existing;

  const script = doc.createElement("script");
  script.setAttribute("type", "text/javascript");
  script.setAttribute("id", MEMED_SCRIPT_ID);
  script.setAttribute("data-color", "#1abc9c");
  script.setAttribute("data-token", token);
  script.setAttribute("data-container", MEMED_CONTAINER_ID);
  script.src = process.env.VUE_APP_DOMINIO_API_MEMED;
  doc.body.appendChild(script);
  return script;
}

/**
 * O hub só serve quando tem a forma que a aplicação usa. Duas armadilhas aqui:
 * `window.MdHub` NÃO existe quando o `onload` do script dispara (nesse momento
 * só existe `MdSinapsePrescricao`; `MdHub` é um alias publicado depois), e o
 * objeto aparece antes de estar completo. Confiar no `onload` era a corrida que
 * fazia a tela de prescrição falhar de forma intermitente.
 */
export function getMemedHub(win = window) {
  const hub = win && win.MdHub;
  if (!hub) return null;

  const usable =
    hub.command &&
    typeof hub.command.send === "function" &&
    hub.event &&
    typeof hub.event.add === "function" &&
    hub.module &&
    typeof hub.module.show === "function";

  return usable ? hub : null;
}

/** A Memed revela o container quando termina de inicializar de verdade. */
export function isMemedModuleVisible(doc = document) {
  const container = doc.getElementById(MEMED_MODULE_CONTAINER_ID);
  return !!container && container.style.display !== "none";
}

// Uma promise por documento: N chamadas concorrentes resultam em UMA injeção.
// A chave é o `doc` (e não uma variável de módulo) para que cada documento
// falso de teste nasça limpo.
const loaders = new WeakMap();

/** Hook de teste: esquece a promise em curso para aquele documento. */
export function resetMemedLoader(doc = document) {
  loaders.delete(doc);
}

/**
 * Garante o SDK carregado e utilizável, resolvendo com o hub.
 *
 * Rejeita com `memed:no-token`, `memed:script-error` ou `memed:hub-timeout` —
 * a distinção não muda o que o usuário lê, mas separa "a Memed recusou este
 * médico" de "o script nem carregou" para quem for investigar.
 */
export function ensureMemedSdk(token, options = {}) {
  const doc = options.doc || document;
  const win = options.win || doc.defaultView || window;
  const timeout = options.timeout || MEMED_HUB_TIMEOUT_MS;
  const pollInterval = options.pollInterval || HUB_POLL_INTERVAL_MS;

  const loaded = getMemedHub(win);
  if (loaded) return Promise.resolve(loaded);

  const pending = loaders.get(doc);
  if (pending) return pending;

  const promise = new Promise((resolve, reject) => {
    const script = loadMemedScript(token, doc);
    if (!script) {
      reject(new Error("memed:no-token"));
      return;
    }

    let poll = null;
    let deadline = null;
    const settle = finish => {
      if (poll) clearInterval(poll);
      if (deadline) clearTimeout(deadline);
      finish();
    };

    script.onerror = () =>
      settle(() => reject(new Error("memed:script-error")));

    poll = setInterval(() => {
      const hub = getMemedHub(win);
      if (hub) settle(() => resolve(hub));
    }, pollInterval);

    deadline = setTimeout(
      () => settle(() => reject(new Error("memed:hub-timeout"))),
      timeout
    );
  });

  // Falhou: esquece a promise para que um segundo clique possa tentar de novo.
  const tracked = promise.catch(error => {
    loaders.delete(doc);
    throw error;
  });

  loaders.set(doc, tracked);
  return tracked;
}
